import { creatorbase } from '@round/api';
import styles from './PlatformPostsFilters.module.css';
import { SearchInput } from '@round/whitelabel-ui';
import { Select } from '@round/whitelabel-ui';
import { countryOptions as baseCountryOptions } from 'utility/constants';
import { Country } from '@round/whitelabel-ui';
import { mapStringListToArray } from 'helpers';
import { DateRangeFilter } from '@round/whitelabel-ui';
import { isNotNullOrUndefined } from 'utility/utility';
import moment from 'moment';
import CampaignImage from '../../../Campaigns/components/CampaignImage/CampaignImage';
import { GenericDropdownOption } from 'App.types';
import { orderingOptions } from './helpers';
import { Dispatch, SetStateAction } from 'react';
import cn from 'classnames';
import { CountSelectedValue } from '@round/whitelabel-ui';
import { None } from '@round/whitelabel-ui';
import { usePostsCountryMetrics } from '@round/creatorbase-helpers/api-hooks';
import { Badge } from '@round/whitelabel-ui';

export type Filters = Pick<
    creatorbase.GetPostsParams,
    'search' | 'country' | 'start_date' | 'end_date' | 'campaign_id' | 'ordering'
>;

export type CampaignOption = GenericDropdownOption<number> & {
    imageUrl?: string;
    projectName?: string;
    brandName?: string;
    managedBy: creatorbase.PublicCampaign['managed_by'];
};

type Props = {
    filters: Filters;
    onChange: Dispatch<SetStateAction<Filters>>;
    campaignOptions?: CampaignOption[];
    reportId?: string;
    publicReportId?: string;
};

const PlatformPostsFilters = ({ filters, onChange, campaignOptions, reportId, publicReportId }: Props) => {
    const selectedCampaignIds = mapStringListToArray(filters.campaign_id).map(Number);
    const dates =
        !filters.start_date && !filters.end_date
            ? null
            : ([filters.start_date, filters.end_date].filter(isNotNullOrUndefined).map((date) => new Date(date)) as
                  | null
                  | [Date]
                  | [Date, Date]);

    const handleFiltersChange = (filters: Partial<Filters>) => {
        onChange((prevFilters) => ({ ...prevFilters, ...filters }));
    };

    const { data: countryMetrics } = usePostsCountryMetrics(
        {
            platform: 'tiktok',
            status: 'live',
            campaign_id: campaignOptions?.map((o) => o.value)?.toString(),
            report_id: reportId,
            publicReportId,
        },
        {
            enabled: Boolean(reportId) || Boolean(campaignOptions?.length),
        }
    );

    const countryOptions = Array.from(baseCountryOptions).sort((a, b) => {
        const getPostCount = (country: string) => {
            return (
                countryMetrics?.metrics.find((item) => item.country?.toUpperCase() === country.toUpperCase())
                    ?.post_count ?? 0
            );
        };

        return getPostCount(b.value) - getPostCount(a.value);
    });

    return (
        <div className={styles.container}>
            <SearchInput value={filters.search} onChange={(search) => handleFiltersChange({ search })} />

            <Select
                className={styles.select}
                isMulti
                hideSelectedOptions={false}
                placeholder="Select countries..."
                value={countryOptions.filter((c) =>
                    mapStringListToArray(filters.country)
                        .map((c) => c.toUpperCase())
                        .includes(c.value.toUpperCase())
                )}
                onChange={(options) =>
                    handleFiltersChange({ country: options?.map((c) => c.value.toUpperCase()).toString() })
                }
                options={countryOptions}
                formatOptionLabel={(option, meta) => {
                    if (meta.context === 'menu') {
                        const count = countryMetrics?.metrics.find((i) => i.country === option.value)?.post_count;

                        return (
                            <div className={styles.countryOption}>
                                <Country className={styles.countryOptionName} code={option.value} />
                                {count ? <Badge className={styles.countryOptionPostCount}>{count}</Badge> : null}
                            </div>
                        );
                    }

                    return option.label;
                }}
                components={{ ValueContainer: CountSelectedValue, MultiValue: None }}
            />

            <Select
                className={styles.select}
                isMulti
                hideSelectedOptions={false}
                placeholder="Select campaigns..."
                options={campaignOptions}
                value={campaignOptions?.filter((c) => selectedCampaignIds.includes(c.value))}
                onChange={(values) => handleFiltersChange({ campaign_id: values?.map((v) => v.value).toString() })}
                formatOptionLabel={(option, { context }) => {
                    if (context === 'value') {
                        return option.label;
                    }

                    const subLabel = `${option.brandName} | ${option.projectName}`;
                    return (
                        <div className={styles.campaignOption}>
                            <CampaignImage
                                imageUrl={option.imageUrl ?? null}
                                managedBy={option.managedBy}
                                platform="tiktok"
                            />

                            <div className={styles.campaignOptionLabels}>
                                <p className={styles.campaignName}>{option.label}</p>
                                <p className={styles.campaignSublabel}>{subLabel}</p>
                            </div>
                        </div>
                    );
                }}
                components={{ ValueContainer: CountSelectedValue, MultiValue: None }}
            />

            <DateRangeFilter
                isClearable
                value={dates}
                onChange={(value) =>
                    handleFiltersChange({
                        start_date: value?.[0] ? moment(value[0]).format('YYYY-MM-DD') : undefined,
                        end_date: value?.[1] ? moment(value[1]).format('YYYY-MM-DD') : undefined,
                    })
                }
            />

            <Select
                className={cn(styles.select, styles.rightSide)}
                isClearable
                placeholder="Order by"
                value={orderingOptions.find((o) => o.value === filters.ordering)}
                onChange={(ordering) => handleFiltersChange({ ordering: ordering?.value })}
                options={orderingOptions}
            />
        </div>
    );
};

export default PlatformPostsFilters;
